import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { FloorPlanRoomOption } from '../models/floorplan-room';
import RoomSelectField from './room-select-field.component';

interface Props {
  readonly open: boolean;
  readonly rooms: FloorPlanRoomOption[];
  /** The storey being edited: its rooms are offered first. */
  readonly storey: number;
  /** Rooms already used by desks on this storey, including ones not yet saved. */
  readonly roomIdsOnThisFloor?: ReadonlySet<string>;
  /** The rooms could not be read, so say that rather than "this office has no rooms". */
  readonly roomsUnavailable?: boolean;
  /** The room to start on, for changing an existing desk's room rather than placing a new one. */
  readonly initialRoomId?: string | null;
  readonly onConfirm: (roomId: string) => void;
  readonly onCancel: () => void;
}

/**
 * Asks which room a desk belongs to.
 *
 * Shown when a desk is placed, because that is the only moment at which the answer is
 * cheap: a desk with no room is drawn on every client and bookable on none, and a plan full
 * of them is discovered weeks later by an employee who cannot reserve a seat that is plainly
 * there. The same choice stays changeable afterwards from the inspector.
 *
 * Rooms that are already full are offered rather than hidden. Capacity is a number an
 * administrator types on the office screen and nothing keeps it in step with how many desks
 * a room actually has, so a full room is a warning about a number to go and fix, not a
 * reason to refuse the desk.
 */
const RoomPickerDialog = ({
  open,
  rooms,
  storey,
  roomIdsOnThisFloor,
  roomsUnavailable = false,
  initialRoomId,
  onConfirm,
  onCancel,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [roomId, setRoomId] = useState<string>(initialRoomId ?? '');

  useEffect(() => {
    if (open) {
      setRoomId(initialRoomId ?? '');
    }
  }, [open, initialRoomId]);

  const selected = rooms.find((room) => room.id === roomId);
  const isFull = selected !== undefined && selected.workplaceCount >= selected.capacity;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{t('floorplan-editor-room-picker-title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText variant="body2">{t('floorplan-editor-room-picker-explanation')}</DialogContentText>

          {rooms.length === 0 ? (
            <Alert severity={roomsUnavailable ? 'error' : 'warning'}>
              {t(roomsUnavailable
                ? 'floorplan-editor-rooms-unavailable'
                : 'floorplan-editor-room-picker-no-rooms')}
            </Alert>
          ) : (
            <RoomSelectField
              rooms={rooms}
              storey={storey}
              value={roomId}
              onChange={setRoomId}
              roomIdsOnThisFloor={roomIdsOnThisFloor}
              autoFocus
            />
          )}

          {isFull && <Alert severity="warning">{t('floorplan-editor-room-at-capacity')}</Alert>}

          {selected !== undefined && !selected.isReservableThroughFloorPlan && (
            <Alert severity="info">{t('floorplan-editor-room-not-reservable-through-floorplan')}</Alert>
          )}

          <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
            {t('floorplan-editor-room-picker-hint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button onClick={(): void => onConfirm(roomId)} variant="contained" disabled={!roomId}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoomPickerDialog;
